import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/user-management.css";
import "../styles/shared-ui.css";
import FormModal from "../components/ui/FormModal";
import ConfirmModal from "../components/ui/ConfirmModal";
import {
  getUsersPaginated,
  createUser,
  patchUser,
  deleteUser,
  resetUserPassword,
} from "../services/userService";
import { getRoles } from "../services/roleService";
import { getCompanyNames } from "../services/companyService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import { useAppContext } from "../context/AppContext";
import { resolveRoleName } from "../utils/auth";

const emptyCreate = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  role_id: "",
  company_id: "",
};

const UserAccessControlPage = ({ currentUser, currentRoleName }) => {
  const { setActiveSection } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [createError, setCreateError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    role_id: "",
    company_id: "",
  });
  const [editError, setEditError] = useState("");
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
  });

  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] =
    useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Updated selector to match the new class name
      if (activeDropdown && !event.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const roleNameById = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      const rid = Number(u?.role_id);
      const name = u?.role?.name || "";
      if (Number.isInteger(rid) && name) m.set(rid, name);
    });
    return m;
  }, [users]);
  const viewerRoleName = useMemo(() => {
    const viaProp = (currentRoleName || "").trim();
    if (viaProp) return viaProp;
    const direct = currentUser?.role || currentUser?.role_name || "";
    if (direct) return String(direct).trim();
    const id = Number(currentUser?.role_id);
    const fromMap = roleNameById.get(id) || "";
    return String(fromMap).trim();
  }, [currentRoleName, roleNameById, currentUser]);
  const isAdminViewer = String(viewerRoleName || "").toUpperCase() === "ADMIN";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm !== "NORMAL_USER";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);
  const rolesForViewer = useMemo(() => {
    if (!isAdminViewer) return roles;
    return (roles || []).filter(
      (r) => String(r?.name || "").toUpperCase() !== "SUPER_ADMIN",
    );
  }, [roles, isAdminViewer]);
  const companyNameById = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => {
      const cid = Number(u?.company_id);
      const name = u?.company?.name || "";
      if (Number.isInteger(cid) && name) m.set(cid, name);
    });
    return m;
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users || [];
  }, [users]);

  const reload = useCallback(
    async (options = {}) => {
      try {
        setLoading(true);
        const pageToLoad = Number(options.page ?? page) || 1;
        const limitToLoad = Number(options.limit ?? limit) || 10;
        const searchToLoad =
          options.search !== undefined ? options.search : debouncedSearch;
        const normalizeRole = (s) =>
          String(s || "")
            .toUpperCase()
            .replace(/[^A-Z]/g, "");
        const roleFromProp = normalizeRole(currentRoleName);
        const roleFromUser = normalizeRole(
          currentUser?.role || currentUser?.role_name,
        );
        const roleFromMap = normalizeRole(
          roleNameById.get(Number(currentUser?.role_id)) || "",
        );
        const useCreatedBy =
          roleFromProp === "ADMIN" ||
          roleFromUser === "ADMIN" ||
          roleFromMap === "ADMIN";

        const result = await getUsersPaginated({
          page: pageToLoad,
          limit: limitToLoad,
          search: searchToLoad,
          useCreatedBy,
        });

        setUsers(Array.isArray(result?.data) ? result.data : []);
        setPagination(
          result?.meta || {
            total: 0,
            page: 1,
            limit: limitToLoad,
            totalPages: 1,
          },
        );
      } catch (e) {
        toast.error(e?.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    },
    [page, limit, debouncedSearch, currentRoleName, currentUser, roleNameById],
  );

  const loadLookups = async () => {
    if (roles.length > 0 && companies.length > 0) return;
    try {
      const [r, c] = await Promise.all([getRoles(), getCompanyNames()]);
      setRoles(Array.isArray(r) ? r : []);
      setCompanies(Array.isArray(c) ? c : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load roles or companies");
    }
  };

  const allSelected = useMemo(
    () => filteredUsers.length > 0 && selectedIds.size === filteredUsers.length,
    [filteredUsers, selectedIds],
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map((u) => u.user_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const startEdit = (user) => {
    loadLookups();
    setEditingId(user.user_id);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      role_id: String(user.role_id ?? ""),
      company_id: String(user.company_id ?? ""),
    });
    setEditError("");
    setEditOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", email: "", role_id: "", company_id: "" });
    setEditError("");
    setEditOpen(false);
  };

  const saveEdit = async (idOverride) => {
    const rawId =
      idOverride !== undefined && idOverride !== null ? idOverride : editingId;
    if (rawId === null || rawId === undefined) {
      toast.error("No user selected for editing");
      return;
    }
    const idNum = Number(rawId);
    if (!Number.isInteger(idNum)) {
      toast.error("Invalid user id");
      return;
    }
    const payload = {
      name: (editForm.name || "").trim(),
      email: (editForm.email || "").trim(),
      role_id: Number(editForm.role_id),
      company_id: editForm.company_id ? Number(editForm.company_id) : null,
    };
    if (!payload.name) {
      setEditError("Name is required");
      return;
    }
    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      setEditError("Valid email is required");
      return;
    }
    if (!Number.isInteger(payload.role_id)) {
      setEditError("Role is required");
      return;
    }
    if (!Number.isInteger(Number(editForm.company_id))) {
      setEditError("Company is required");
      return;
    }
    if (isAdminViewer) {
      const nextRoleName = String(
        roleNameById.get(Number(payload.role_id)) || "",
      ).toUpperCase();
      if (nextRoleName === "SUPER_ADMIN") {
        setEditError("You are not allowed to assign SUPER_ADMIN");
        return;
      }
    }
    try {
      await patchUser(idNum, payload);
      setPage(1);
      await reload({ page: 1 });
      toast.success(`User "${payload.name}" has been updated successfully!`);
      cancelEdit();
    } catch (e) {
      setEditError(e?.message || "Failed to update user");
      toast.error(e?.message || "Failed to update user");
    }
  };

  const removeSingle = (id) => {
    const user = users.find((u) => u.user_id === id);
    const userName = user?.name || user?.email || "User";
    setConfirmDelete({
      open: true,
      id,
      name: userName,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id, name: deletedName } = confirmDelete;
    setConfirmDelete({ open: false, id: null, name: "" });

    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.user_id !== id));
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      toast.success(`User "${deletedName}" has been deleted successfully!`);
    } catch (e) {
      toast.error(e?.message || "Failed to delete user");
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setConfirmDelete({ open: false, id: null, name: "" });

    try {
      // simple sequential delete to keep consistent with available service
      for (const id of ids) {
        // eslint-disable-line no-restricted-syntax
        // eslint-disable-next-line no-await-in-loop
        await deleteUser(id);
      }
      setUsers((prev) => prev.filter((u) => !selectedIds.has(u.user_id)));
      setSelectedIds(new Set());
      toast.success(`${ids.length} user${ids.length > 1 ? "s" : ""} deleted successfully!`);
    } catch (e) {
      toast.error(e?.message || "Failed to delete selected users");
    }
  };

  useEffect(() => {
    if (!currentUser?.user_id) return;
    const key = `${currentUser.user_id}:${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [currentUser?.user_id, page, limit, debouncedSearch, reload]);

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);
  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);
  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  const openCreate = () => {
    loadLookups();
    setCreateForm({ ...emptyCreate });
    setCreateError("");
    setCreateOpen(true);
  };
  const closeCreate = () => {
    setCreateOpen(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleCreate = async () => {
    try {
      setCreateError("");
      const payload = {
        name: (createForm.name || "").trim(),
        email: (createForm.email || "").trim(),
        password: (createForm.password || "").trim(),
        confirmPassword: (createForm.confirmPassword || "").trim(),
        role_id: Number(createForm.role_id),
        company_id:
          createForm.company_id ? Number(createForm.company_id) : null,
      };
      if (!payload.name) {
        setCreateError("Name is required");
        return;
      }
      if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
        setCreateError("Valid email is required");
        return;
      }
      if (!payload.password) {
        setCreateError("Password is required");
        return;
      }
      if (!payload.confirmPassword) {
        setCreateError("Confirm password is required");
        return;
      }
      if (payload.password !== payload.confirmPassword) {
        setCreateError("Passwords do not match");
        return;
      }
      if (!Number.isInteger(payload.role_id)) {
        setCreateError("Role is required");
        return;
      }
      if (!Number.isInteger(Number(createForm.company_id))) {
        setCreateError("Company is required");
        return;
      }
      if (isAdminViewer) {
        const nextRoleName = String(
          roleNameById.get(Number(payload.role_id)) || "",
        ).toUpperCase();
        if (nextRoleName === "SUPER_ADMIN") {
          setCreateError("You are not allowed to create a SUPER_ADMIN");
          return;
        }
      }

      await createUser(payload);
      setPage(1);
      await reload({ page: 1 });
      toast.success(`User "${payload.name}" has been created successfully!`);
      closeCreate();
    } catch (e) {
      setCreateError(e?.message || "Failed to create user");
      toast.error(e?.message || "Failed to create user");
    }
  };

  const openResetPassword = (user) => {
    setResetPasswordUser(user);
    setResetPasswordForm({ password: "", confirmPassword: "" });
    setResetPasswordError("");
    setResetPasswordOpen(true);
  };

  const closeResetPassword = () => {
    setResetPasswordOpen(false);
    setResetPasswordUser(null);
    setShowResetPassword(false);
    setShowResetConfirmPassword(false);
  };

  const handleResetPassword = async () => {
    try {
      setResetPasswordError("");
      const password = (resetPasswordForm.password || "").trim();
      const confirmPassword = (resetPasswordForm.confirmPassword || "").trim();

      if (!password) {
        setResetPasswordError("Password is required");
        return;
      }
      if (!confirmPassword) {
        setResetPasswordError("Confirm password is required");
        return;
      }
      if (password !== confirmPassword) {
        setResetPasswordError("Passwords do not match");
        return;
      }
      if (password.length < 6) {
        setResetPasswordError("Password must be at least 6 characters");
        return;
      }

      await resetUserPassword(resetPasswordUser.user_id, {
        password,
        confirmPassword,
      });
      toast.success(`Password for "${resetPasswordUser.name || resetPasswordUser.email}" has been reset successfully!`);
      closeResetPassword();
    } catch (e) {
      setResetPasswordError(e?.message || "Failed to reset password");
      toast.error(e?.message || "Failed to reset password");
    }
  };

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">User Management</h2>
            <p className="page-subtitle">View all users and create new ones</p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, email, role, company"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button type="button" className="primary-btn" onClick={openCreate}>
              Create User
            </button>
            {/* "Delete Selected" is now in the selection-specific toolbar below */}
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="users-pagination-top">
          <label htmlFor="users-limit" className="users-pagination-label">
            Rows per page:
          </label>
          <select
            id="users-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "var(--accent-tint-50)",
              borderRadius: "0.75rem",
              border: "1px solid var(--accent-tint-200)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span
                style={{
                  fontWeight: "600",
                  color: "var(--accent-ink-800)",
                  fontSize: "0.9375rem",
                }}
              >
                {selectedIds.size} {selectedIds.size === 1 ? "user" : "users"}{" "}
                selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button className="danger-btn sm" onClick={bulkDelete}>
                  Delete Selected
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}
        {loading ?
          <div className="no-results">Loading users…</div>
        : <div className="users-table-container">
            <div className="users-table">
              <div className="users-table-wrapper">
                <div className="users-table-header">
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </div>
                  <div className="cell">Name</div>
                  <div className="cell">Email</div>
                  <div className="cell">Role</div>
                  <div className="cell">Company</div>
                  <div className="cell actions">Actions</div>
                </div>
                {filteredUsers.length === 0 ? null : (
                  filteredUsers.map((u) => {
                    const roleName = (
                      u?.role?.name ||
                      roleNameById.get(Number(u.role_id)) ||
                      ""
                    )
                      .toUpperCase()
                      .replace(/[^A-Z_]/g, "");
                    let badgeClass = "role-badge normal-user";
                    if (roleName === "SUPER_ADMIN")
                      badgeClass = "role-badge super-admin";
                    else if (roleName === "ADMIN")
                      badgeClass = "role-badge admin";

                    return (
                      <div
                        key={u.user_id}
                        className={`users-table-row ${
                          selectedIds.has(u.user_id) ? "selected" : ""
                        }`}
                      >
                        <div className="cell checkbox">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(u.user_id)}
                            onChange={() => toggleSelect(u.user_id)}
                          />
                        </div>
                        <div className="cell user-name">{u.name}</div>
                        <div className="cell user-email">{u.email}</div>
                        <div className="cell user-role">
                          <span className={badgeClass}>
                            {u?.role?.name ||
                              roleNameById.get(Number(u.role_id)) ||
                              "—"}
                          </span>
                        </div>
                        <div className="cell user-company">
                          {u?.company?.name ||
                            companyNameById.get(Number(u.company_id)) ||
                            "—"}
                        </div>
                        <div className="cell actions">
                          <div
                            className="actions-menu-wrapper"
                            style={{
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            <button
                              className="action-menu-btn"
                              aria-haspopup="true"
                              aria-expanded={activeDropdown === u.user_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (activeDropdown === u.user_id) {
                                  setActiveDropdown(null);
                                } else {
                                  setDropdownDirection(
                                    getActionDropdownDirection(e.currentTarget),
                                  );
                                  setActiveDropdown(u.user_id);
                                }
                              }}
                            >
                              Actions ▾
                            </button>
                            {activeDropdown === u.user_id && (
                              <div
                                className={`action-dropdown-menu${dropdownDirection === "up" ? " dropdown-up" : ""}`}
                                role="menu"
                              >
                                <button
                                  className="action-menu-item"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    startEdit(u);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="action-menu-item"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    openResetPassword(u);
                                  }}
                                >
                                  Reset Password
                                </button>
                                <button
                                  className="action-menu-item danger"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    removeSingle(u.user_id);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            {filteredUsers.length === 0 && (
              <div className="no-results">No users found</div>
            )}
          </div>
        }

        <div className="users-pagination-bottom">
          <div className="users-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="users-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="secondary-btn sm"
                    aria-hidden="true"
                  >
                    ...
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <FormModal
        open={editOpen}
        title="Edit User"
        onCancel={cancelEdit}
        size="compact"
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => saveEdit()}
            >
              Save
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </>
        }
      >
        {editError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {editError}
          </div>
        )}
        <div className="user-create-form">
          <div className="form-row">
            <div className="field">
              <label htmlFor="eu-name">Name</label>
              <input
                id="eu-name"
                type="text"
                placeholder="Enter full name"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div className="field">
              <label htmlFor="eu-email">Email</label>
              <input
                id="eu-email"
                type="email"
                placeholder="Enter email address"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, email: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="eu-company">Company</label>
              <select
                id="eu-company"
                value={editForm.company_id}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, company_id: e.target.value }))
                }
              >
                <option value="">Select company</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="eu-role">Role</label>
              <select
                id="eu-role"
                value={editForm.role_id}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, role_id: e.target.value }))
                }
              >
                <option value="">Select role</option>
                {rolesForViewer.map((r) => (
                  <option key={r.role_id} value={r.role_id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </FormModal>

      <FormModal
        open={createOpen}
        title="Create User"
        onCancel={closeCreate}
        size="compact"
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={handleCreate}
            >
              Create
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={closeCreate}
            >
              Cancel
            </button>
          </>
        }
      >
        {createError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {createError}
          </div>
        )}
        <div className="user-create-form">
          <div className="form-row">
            <div className="field">
              <label htmlFor="cu-name">Name</label>
              <input
                id="cu-name"
                type="text"
                placeholder="Enter full name"
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({ ...createForm, name: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label htmlFor="cu-email">Email</label>
              <input
                id="cu-email"
                type="email"
                placeholder="Enter email address"
                value={createForm.email}
                onChange={(e) =>
                  setCreateForm({ ...createForm, email: e.target.value })
                }
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="cu-password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="cu-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={createForm.password}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, password: e.target.value })
                  }
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ?
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  : <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  }
                </button>
              </div>
            </div>
            <div className="field">
              <label htmlFor="cu-confirm">Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  id="cu-confirm"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={createForm.confirmPassword}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      confirmPassword: e.target.value,
                    })
                  }
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  title={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ?
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  : <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  }
                </button>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="cu-company">Company</label>
              <select
                id="cu-company"
                value={createForm.company_id}
                onChange={(e) =>
                  setCreateForm({ ...createForm, company_id: e.target.value })
                }
              >
                <option value="">Select company</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cu-role">Role</label>
              <select
                id="cu-role"
                value={createForm.role_id}
                onChange={(e) =>
                  setCreateForm({ ...createForm, role_id: e.target.value })
                }
              >
                <option value="">Select role</option>
                {rolesForViewer.map((r) => (
                  <option key={r.role_id} value={r.role_id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </FormModal>
      <ConfirmModal
        open={confirmDelete.open}
        title={confirmDelete.isBulk ? "Confirm Bulk Delete" : "Confirm Delete"}
        message={
          confirmDelete.isBulk ?
            `Are you sure you want to delete ${confirmDelete.count} ${
              confirmDelete.count === 1 ? "user" : "users"
            }? This action cannot be undone.`
          : `Are you sure you want to delete the user "${confirmDelete.name}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={
          confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
        }
        onCancel={() => setConfirmDelete({ open: false, id: null, name: "" })}
        isDanger={true}
      />

      <FormModal
        open={resetPasswordOpen}
        title={`Reset Password for ${resetPasswordUser?.name || "User"}`}
        onCancel={closeResetPassword}
        size="compact"
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={handleResetPassword}
            >
              Reset Password
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={closeResetPassword}
            >
              Cancel
            </button>
          </>
        }
      >
        {resetPasswordError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {resetPasswordError}
          </div>
        )}
        <div className="reset-password-form">
          <div className="field">
            <label htmlFor="rp-password">New Password</label>
            <div className="password-input-wrapper">
              <input
                id="rp-password"
                type={showResetPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={resetPasswordForm.password}
                onChange={(e) =>
                  setResetPasswordForm({
                    ...resetPasswordForm,
                    password: e.target.value,
                  })
                }
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowResetPassword(!showResetPassword)}
                title={showResetPassword ? "Hide password" : "Show password"}
                aria-label={
                  showResetPassword ? "Hide password" : "Show password"
                }
              >
                {showResetPassword ?
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                : <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                }
              </button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="rp-confirm">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                id="rp-confirm"
                type={showResetConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={resetPasswordForm.confirmPassword}
                onChange={(e) =>
                  setResetPasswordForm({
                    ...resetPasswordForm,
                    confirmPassword: e.target.value,
                  })
                }
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() =>
                  setShowResetConfirmPassword(!showResetConfirmPassword)
                }
                title={
                  showResetConfirmPassword ? "Hide password" : "Show password"
                }
                aria-label={
                  showResetConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showResetConfirmPassword ?
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                : <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                }
              </button>
            </div>
          </div>
        </div>
      </FormModal>
    </div>
  );
};

export default UserAccessControlPage;

